import { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import type { Href } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { clearSyntheticSessionForRedirect, supabase } from '../lib/supabase';
import { useUnregisterPushNotifications } from '../hooks/usePushNotifications';
import { trackAuthSignedOut } from '../lib/product-events';
import { performSignOut } from '../lib/sign-out';
import { trpc } from '../lib/trpc';
import { markScreenNavigationStarted } from '../hooks/useScreenReadyTelemetry';
import { HelpSupportModal } from './HelpSupportModal';

type NavActionKey = 'ledger' | 'firePlan';

interface NavItem {
  route: string;
  label: string;
  icon: React.ComponentProps<typeof Ionicons>['name'];
  iconActive: React.ComponentProps<typeof Ionicons>['name'];
  actionKey?: NavActionKey;
}

const NAV_ITEMS: NavItem[] = [
  { route: '/(app)', label: 'Dashboard', icon: 'grid-outline', iconActive: 'grid' },
  { route: '/(app)/transactions', label: 'Transactions', icon: 'receipt-outline', iconActive: 'receipt', actionKey: 'ledger' },
  { route: '/(app)/accounts', label: 'Balance Sheet', icon: 'wallet-outline', iconActive: 'wallet' },
  { route: '/(app)/budget', label: 'Budget', icon: 'pie-chart-outline', iconActive: 'pie-chart' },
  { route: '/(app)/fire-plan', label: 'FIRE Plan', icon: 'flame-outline', iconActive: 'flame', actionKey: 'firePlan' },
  { route: '/(app)/ai-chat', label: 'AI Advisor', icon: 'sparkles-outline', iconActive: 'sparkles' },
];

const BOTTOM_NAV: NavItem[] = [
  { route: '/(app)/integrations', label: 'Integrations', icon: 'layers-outline', iconActive: 'layers' },
  { route: '/(app)/profile', label: 'Profile', icon: 'person-outline', iconActive: 'person' },
  { route: '/(app)/settings', label: 'Settings', icon: 'settings-outline', iconActive: 'settings' },
];

const ICON_COLOR_INACTIVE = 'rgba(255,255,255,0.55)';
const ICON_COLOR_ACTIVE = '#ffffff';

function isActive(pathname: string, route: string): boolean {
  if (route === '/(app)') return pathname === '/(app)' || pathname === '/';
  return pathname.startsWith(route);
}

function formatActionCount(count: number) {
  return count > 99 ? '99+' : String(count);
}

function getNavTarget(item: NavItem, actionCount: number): Href {
  if (item.actionKey === 'ledger' && actionCount > 0) {
    return {
      pathname: '/(app)/transactions',
      params: { needsReview: '1' },
    } as Href;
  }

  return item.route as Href;
}

function ActionCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;

  return (
    <View className="ml-auto min-w-[18px] h-[18px] rounded-full bg-amber-400 px-1.5 items-center justify-center">
      <Text className="text-[10px] font-bold text-amber-950 leading-[12px] tabular-nums">
        {formatActionCount(count)}
      </Text>
    </View>
  );
}

interface WebSidebarProps {
  isDemo?: boolean;
  accessLimited?: boolean;
  actionCounts?: Partial<Record<NavActionKey, number>>;
}

export function WebSidebar({ isDemo, accessLimited, actionCounts }: WebSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const unregisterPushNotifications = useUnregisterPushNotifications();
  const [supportOpen, setSupportOpen] = useState(false);
  const utils = trpc.useUtils();

  const prefetchNavItem = (item: NavItem) => {
    switch (item.route) {
      case '/(app)/accounts':
        void Promise.all([
          utils.accounts.list.prefetch(),
          utils.assets.list.prefetch(),
          utils.liabilities.list.prefetch(),
        ]);
        break;
      case '/(app)/budget':
        void Promise.all([
          utils.budget.getEntries.prefetch(),
          utils.budget.averages.prefetch(),
          utils.budget.currentMonthActuals.prefetch(),
          utils.budget.currentYearActuals.prefetch(),
          utils.budget.perDiem.prefetch(),
        ]);
        break;
      case '/(app)/fire-plan':
        void Promise.all([
          utils.fire.getPlan.prefetch(),
          utils.profiles.list.prefetch(),
        ]);
        break;
      case '/(app)/transactions':
        void Promise.all([
          utils.transactions.list.prefetch({ page: 1, pageSize: 50 }),
          utils.transactions.reviewCount.prefetch(),
        ]);
        break;
      default:
        break;
    }
  };

  const handleLeaveDemo = () => {
    trackAuthSignedOut({
      route: pathname,
      source: 'demo_sidebar_return_home',
      metadata: { isDemo: true },
    });
    if (typeof window !== 'undefined') {
      clearSyntheticSessionForRedirect();
      window.location.replace('/');
    } else {
      void supabase.auth.signOut().then(() => {
        router.replace('/');
      });
    }
  };

  const handleSignOut = async () => {
    await performSignOut({
      source: 'sidebar_subscription_required',
      route: pathname,
      unregisterPushNotifications,
    });
    router.replace('/(auth)/login');
  };

  const renderNavItem = (item: NavItem, disabled = false) => {
    const active = isActive(pathname, item.route);
    const actionCount = item.actionKey ? actionCounts?.[item.actionKey] ?? 0 : 0;

    return (
      <Pressable
        key={item.route}
        onHoverIn={() => {
          if (!disabled) prefetchNavItem(item);
        }}
        onPressIn={() => {
          if (!disabled) {
            markScreenNavigationStarted(item.route);
            prefetchNavItem(item);
          }
        }}
        onPress={() => {
          if (!disabled) router.push(getNavTarget(item, actionCount));
        }}
        disabled={disabled}
        className={`web-nav-item flex-row items-center gap-2.5 rounded-[10px] px-3 py-2.5 ${
          active ? 'bg-white/[0.11]' : ''
        } ${disabled ? 'opacity-40' : ''}`}
      >
        <Ionicons
          name={active ? item.iconActive : item.icon}
          size={16}
          color={active ? ICON_COLOR_ACTIVE : ICON_COLOR_INACTIVE}
        />
        <Text
          className={`text-[13.5px] flex-1 ${
            active ? 'font-semibold text-white' : 'font-medium text-white/[0.55]'
          }`}
        >
          {item.label}
        </Text>
        <ActionCountBadge count={actionCount} />
      </Pressable>
    );
  };

  const renderSupportItem = (disabled = false) => (
    <Pressable
      onPress={() => {
        if (!disabled) setSupportOpen(true);
      }}
      disabled={disabled}
      className={`web-nav-item flex-row items-center gap-2.5 rounded-[10px] px-3 py-2.5 ${
        disabled ? 'opacity-40' : ''
      }`}
    >
      <Ionicons name="help-circle-outline" size={16} color={ICON_COLOR_INACTIVE} />
      <Text className="text-[13.5px] flex-1 font-medium text-white/[0.55]">
        Help & Support
      </Text>
    </Pressable>
  );

  return (
    <>
    <View className="w-[220px] bg-sidebar flex-col justify-between py-6 px-3">
      <View>
        {/* Brand */}
        <View className="mb-7 px-2">
          <View className="flex-row items-center gap-2.5">
            <View className="w-7 h-7 rounded-lg bg-forest-600 items-center justify-center">
              <Ionicons name="leaf" size={14} color="#ffffff" />
            </View>
            <View>
              <View className="flex-row items-center gap-2">
                <Text className="text-[15px] font-bold text-white tracking-tight">
                  Per Diem
                </Text>
                {isDemo ? (
                  <View className="rounded-full border border-amber-400/40 bg-amber-400/15 px-2 py-0.5">
                    <Text className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-amber-300">
                      Demo
                    </Text>
                  </View>
                ) : null}
              </View>
              <Text className="text-[11px] text-white/35 mt-0.5">
                Every day matters
              </Text>
            </View>
          </View>
        </View>

        {accessLimited ? (
          <View className="rounded-2xl border border-white/[0.08] bg-white/[0.04] px-3 py-3">
            <Text className="text-[13px] font-semibold text-white">
              Subscription required
            </Text>
            <Text className="mt-1 text-[11px] leading-4 text-white/55">
              Subscribe in Settings to restore app access.
            </Text>
          </View>
        ) : (
          <View className="gap-0.5">
            {NAV_ITEMS.map((item) => renderNavItem(item))}
          </View>
        )}
      </View>

      {/* Bottom */}
      <View className="gap-0.5 border-t border-white/[0.07] pt-4">
        {isDemo ? (
          <Pressable
            onPress={handleLeaveDemo}
            className="mb-3 rounded-2xl border border-forest-500/40 bg-forest-600/15 px-3 py-3"
          >
            <View className="flex-row items-center gap-3">
              <View className="h-8 w-8 items-center justify-center rounded-full bg-forest-600">
                <Ionicons name="arrow-back" size={16} color="#ffffff" />
              </View>
              <View className="flex-1">
                <Text className="text-[13px] font-semibold text-white">
                  Learn more about Per Diem
                </Text>
                <Text className="mt-0.5 text-[11px] text-white/55">
                  Leave the demo
                </Text>
              </View>
            </View>
          </Pressable>
        ) : null}
        {accessLimited ? (
          <>
            {renderSupportItem()}
            {renderNavItem(
              { route: '/(app)/settings', label: 'Settings', icon: 'settings-outline', iconActive: 'settings' },
            )}
            <Pressable
              onPress={() => {
                void handleSignOut();
              }}
              className="web-nav-item flex-row items-center gap-2.5 rounded-[10px] px-3 py-2.5"
            >
              <Ionicons name="log-out-outline" size={16} color="#fca5a5" />
              <Text className="text-[13.5px] flex-1 font-medium text-red-200">
                Sign out
              </Text>
            </Pressable>
          </>
        ) : (
          <>
            {renderSupportItem(isDemo)}
            {BOTTOM_NAV.map((item) => renderNavItem(item, isDemo))}
          </>
        )}
      </View>
    </View>
    <HelpSupportModal visible={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}
